import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { REVIEWS, HOTEL } from "../data/site";

function ReviewCard({ r }) {
  return (
    <article
      className="glass-card rounded-3xl p-7 sm:p-8 min-h-[260px] flex flex-col"
      data-testid="review-card"
    >
      <div className="flex items-center justify-between">
        <Quote size={24} className="text-[#D4AF37]" />
        {r.tag ? (
          <span className="text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 rounded-full bg-[#7A1A24]/10 dark:bg-[#D4AF37]/10 text-[#7A1A24] dark:text-[#E6C555]">
            {r.tag}
          </span>
        ) : null}
      </div>
      <p className="mt-4 text-foreground/90 leading-relaxed text-[15px] flex-1">
        “{r.body}”
      </p>
      <div className="mt-6 flex items-center gap-3">
        <div className="h-11 w-11 rounded-full bg-gradient-to-br from-[#7A1A24] to-[#5C111A] grid place-items-center text-[#D4AF37] font-display text-sm">
          {r.initials}
        </div>
        <div className="flex-1">
          <div className="font-medium text-foreground text-sm">{r.name}</div>
          <div className="text-xs text-muted-foreground">{r.date}</div>
        </div>
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <Star
              key={s}
              size={13}
              className={s <= r.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "fill-transparent text-[#D4AF37]/30"}
            />
          ))}
        </div>
      </div>
    </article>
  );
}

export default function Reviews() {
  const [index, setIndex] = useState(0);
  const [perView, setPerView] = useState(1);

  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      setPerView(w >= 1024 ? 3 : w >= 640 ? 2 : 1);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  const total = REVIEWS.length;
  const maxIndex = Math.max(0, total - perView);

  const next = useCallback(() => setIndex((i) => Math.min(i + 1, maxIndex)), [maxIndex]);
  const prev = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), []);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i >= maxIndex ? 0 : i + 1));
    }, 6000);
    return () => clearInterval(id);
  }, [maxIndex]);

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="section-padding bg-beige/40 dark:bg-card overflow-x-hidden"
    >
      <div className="container-x max-w-[100vw]">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <span className="eyebrow">Guest Reviews</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight text-foreground">
              Words from <span className="italic text-[#7A1A24] dark:text-[#D4AF37]">our guests.</span>
            </h2>
            <div className="mt-5 flex items-center gap-3">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4].map((s) => (
                  <Star key={s} size={18} className="fill-[#D4AF37] text-[#D4AF37]" />
                ))}
                <Star size={18} className="fill-[#D4AF37]/40 text-[#D4AF37]" />
              </div>
              <span className="font-display text-2xl text-foreground">{HOTEL.rating}</span>
              <span className="text-sm text-muted-foreground">
                · {HOTEL.reviewCount} verified reviews
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={prev}
              disabled={index === 0}
              data-testid="reviews-prev"
              aria-label="Previous review"
              className="h-11 w-11 rounded-full border border-border bg-card grid place-items-center hover:border-[#D4AF37] disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={next}
              disabled={index >= maxIndex}
              data-testid="reviews-next"
              aria-label="Next review"
              className="h-11 w-11 rounded-full border border-border bg-card grid place-items-center hover:border-[#D4AF37] disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="mt-12 overflow-x-hidden -mx-3">
          <motion.div
            className="flex"
            animate={{ x: `-${(100 / perView) * index}%` }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            {REVIEWS.map((r) => (
              <div
                key={r.name}
                className="shrink-0 px-3"
                style={{ width: `${100 / perView}%` }}
              >
                <ReviewCard r={r} />
              </div>
            ))}
          </motion.div>
        </div>

        <div className="mt-8 flex justify-center gap-1.5">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              aria-label={`Go to review group ${i + 1}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "w-8 bg-[#D4AF37]" : "w-1.5 bg-foreground/20"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
